import {
  atom,
  RecoilState,
  SetterOrUpdater,
  useRecoilState,
  useSetRecoilState,
} from 'recoil';

import { SeriesOptionsType } from 'highcharts';

import * as K from 'common/constants';
import { ChartSelectionRange } from 'common/types';

/**
 * This interface declares the typescript interface of the global state object for the useGlobal hook from the reactn
 * library. Any new keys for useGlobal must be defined as a {@link GLOBAL_KEY} and typed here. Despite the fact we
 * no longer use reactn, we opted to keep and reuse this interface for the time being to reduce the scope of changes.
 */
export interface State {
  [K.GLOBAL_KEY.CHART_SELECTION_RANGE]:        ChartSelectionRange;
  [K.GLOBAL_KEY.DASHBOARD_FACET_CACHE_IDS]:    string[];
  [K.GLOBAL_KEY.FACET_DATA]:                   any;
  [K.GLOBAL_KEY.GRAPH_SERIES_DATA]:            Array<SeriesOptionsType>;
  [K.GLOBAL_KEY.LOG_ENTRY_ASC_THRESHOLDS]:     boolean[];
  [K.GLOBAL_KEY.LOG_ENTRY_DESC_THRESHOLDS]:    boolean[];
  [K.GLOBAL_KEY.LOG_ENTRY_MEASUREMENT_CACHE]:  Record<string, number>;
  [K.GLOBAL_KEY.LOG_ENTRY_PAGINATION_CURSORS]: string[];
}

type AtomCollection<T> = {
  [P in keyof T]?: RecoilState<T[P]>
};

export const atoms: AtomCollection<State> = {};

/**
 * This function reimplements reactn setGlobal. It creates a collection of recoil atoms, one for each state property.
 */
export function setGlobal(newGlobalState: Partial<State>): void {
  Object.keys(newGlobalState)
    .forEach((key) => {
      atoms[key as (keyof State)] = atom({
        key,
        default: newGlobalState[key as (keyof State)],
        dangerouslyAllowMutability: true,
      });
    });
}

function throwOnGlobalStateAccessAttempt<K extends keyof State>(key: K): never | void {
  if (!key) {
    throw new Error('useGlobal adapter does not allow accessing the entire global state');
  }
}

/**
 * This function reimplements reactn useGlobal hook. It tries to use atoms created by setGlobal function.
 */
export function useGlobal<K extends keyof State>(key: K): [State[K], SetterOrUpdater<State[K]>] {
  throwOnGlobalStateAccessAttempt(key);

  if (!atoms[key]) {
    atoms[key] = atom({
      key,
      default: undefined,
      dangerouslyAllowMutability: true,
    }) as RecoilState<any>;
  }
  return useRecoilState(atoms[key] as RecoilState<any>);
}

/**
 * Allows components to update global state without subscribing to values.
 * Subscription is performance costly, and this function is the way to avoid it.
 *
 * @param key part of Scalyr's web application global state.
 */
export function useGlobalSetState<K extends keyof State>(key: K): SetterOrUpdater<State[K]> {
  throwOnGlobalStateAccessAttempt(key);

  return useSetRecoilState(atoms[key] as RecoilState<State[K]>);
}
